import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { CenteredModal } from '../../../UI/Modal/CenteredModal';
import { Input } from '../../../UI/V3/Forms/Input/Input';
import { Checkbox } from '../../../UI/V3/Forms/Checkbox/Checkbox';
import Button from '../../../UI/V3/Button/Button';
import styles from './AddApiKeyModal.module.scss';
import { createApiKeyThunk, updateApiKeyThunk } from 'actions/apiKeys/ApiKeysActions';
import { fetchLocationsThunk } from 'actions/location/LocationActions';
import { showSuccessThunk } from '@spike/notifications-action';
import { RootState } from 'store';
import { RootAction } from 'actions/ActionTypes';
import { ApiKey, ApiScopeAction, API_SCOPE_ACTION_LABELS, API_SCOPE_RESOURCES, buildScope } from 'model/apiKeys';

interface AddApiKeyModalProps {
    isOpen: boolean;
    organizationId?: number;
    editingApiKey?: ApiKey | null;
    onClose: () => void;
    /**
     * Called after a successful create with the secret the backend returned
     * once. The caller is responsible for revealing it; it is never stored.
     */
    // eslint-disable-next-line no-unused-vars
    onCreated: (apiKey: ApiKey, secret: string) => void;
    onUpdated: () => void;
}

interface ApiKeyFormErrors {
    name?: string;
    scopes?: string;
    locations?: string;
    expiresAt?: string;
}

/**
 * Earliest expiry the backend would honour. A key whose `expires_at` has
 * already passed is rejected by `APIProject#usable?`, so a date of today would
 * create a key that is born unusable.
 */
const earliestExpiry = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
};

export const AddApiKeyModal: React.FC<AddApiKeyModalProps> = ({
    isOpen,
    organizationId,
    editingApiKey,
    onClose,
    onCreated,
    onUpdated
}) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { locations } = useSelector((state: RootState) => state.locations);

    const [name, setName] = useState('');
    const [scopes, setScopes] = useState<Array<string>>([]);
    const [allLocations, setAllLocations] = useState(true);
    const [locationIds, setLocationIds] = useState<Array<number>>([]);
    const [expiresAt, setExpiresAt] = useState('');
    const [errors, setErrors] = useState<ApiKeyFormErrors>({});
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const isEditing = Boolean(editingApiKey);

    // The location picker needs the organization's locations.
    useEffect(() => {
        if (isOpen && locations.length === 0) {
            dispatch(fetchLocationsThunk());
        }
    }, [dispatch, isOpen, locations.length]);

    // Populate when editing, reset when creating.
    useEffect(() => {
        if (editingApiKey) {
            setName(editingApiKey.name);
            setScopes(editingApiKey.scopes);
            setAllLocations(editingApiKey.allLocations);
            setLocationIds(editingApiKey.locationIds);
            // The backend returns a datetime; the date input takes YYYY-MM-DD.
            setExpiresAt(editingApiKey.expiresAt ? editingApiKey.expiresAt.slice(0, 10) : '');
        } else {
            setName('');
            setScopes([]);
            setAllLocations(true);
            setLocationIds([]);
            setExpiresAt('');
        }
        setErrors({});
    }, [editingApiKey, isOpen]);

    // Resources whose endpoints do not exist yet are filtered out: an admin
    // cannot act on them, and the scope would be granted with nothing to
    // answer it. See API_SCOPE_RESOURCES for which ones and why.
    const availableResources = useMemo(() => API_SCOPE_RESOURCES.filter(resource => !resource.unavailable), []);

    const hasScope = (scope: string) => scopes.includes(scope);

    const toggleScope = (scope: string, checked: boolean) => {
        setScopes(previous => {
            if (checked) {
                return previous.includes(scope) ? previous : [...previous, scope];
            }
            return previous.filter(existing => existing !== scope);
        });

        setErrors(previous => ({ ...previous, scopes: undefined }));
    };

    /**
     * Write implies read: a key that can create a client but not read one back
     * is not useful, and the backend treats the two scopes independently.
     *
     * Only applies to resources that have both actions. `reports` is read-only
     * and `exports` only takes `manage`, so there is nothing to pair there.
     */
    const handleScopeChange = (resource: string, action: ApiScopeAction, checked: boolean) => {
        toggleScope(buildScope(resource, action), checked);

        if (action === 'write' && checked) {
            toggleScope(buildScope(resource, 'read'), true);
        }
        if (action === 'read' && !checked) {
            toggleScope(buildScope(resource, 'write'), false);
        }
    };

    const toggleLocation = (locationId: number, checked: boolean) => {
        setLocationIds(previous =>
            checked ? [...previous, locationId] : previous.filter(existing => existing !== locationId)
        );
        setErrors(previous => ({ ...previous, locations: undefined }));
    };

    const validate = (): boolean => {
        const nextErrors: ApiKeyFormErrors = {};

        if (!name.trim()) {
            nextErrors.name = 'Name is required';
        } else if (name.length > 100) {
            nextErrors.name = 'Name must not exceed 100 characters';
        }

        if (scopes.length === 0) {
            nextErrors.scopes = 'Select at least one permission';
        }

        // An empty allowlist means "every location" on the backend, so an
        // explicit selection with nothing picked has to be caught here.
        if (!allLocations && locationIds.length === 0) {
            nextErrors.locations = 'Select at least one location, or grant access to all of them';
        }

        if (expiresAt && expiresAt < earliestExpiry()) {
            nextErrors.expiresAt = 'The expiration date must be in the future';
        }

        setErrors(nextErrors);

        // The form is taller than the modal, so the submit button sits well
        // below the first field. Without this, failing validation looks like
        // nothing happened: the errors render above the fold and the view
        // stays where the button is.
        if (Object.keys(nextErrors).length > 0) {
            window.requestAnimationFrame(() => {
                const firstError = formRef.current?.querySelector('[class*="hasError"], [class*="fieldError"]');
                firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }

        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = async () => {
        if (!organizationId || !validate()) return;

        setIsSaving(true);

        const payload = {
            name: name.trim(),
            scopes,
            allLocations,
            locationIds,
            // Empty means the key never expires.
            expiresAt: expiresAt || null
        };

        try {
            if (editingApiKey) {
                await dispatch(updateApiKeyThunk(organizationId, editingApiKey.id, payload));
                dispatch(showSuccessThunk('API Key updated') as any);
                onUpdated();
                onClose();
            } else {
                const { apiKey, secret } = await dispatch(createApiKeyThunk(organizationId, payload));
                onCreated(apiKey, secret);
            }
        } catch (error) {
            // The thunk already surfaced the message through the error toast.
        } finally {
            setIsSaving(false);
        }
    };

    const renderScopeRow = ({ resource, label, actions }: (typeof API_SCOPE_RESOURCES)[number]) => (
        <div
            key={resource}
            className={styles.scopeRow}
        >
            <span className={styles.scopeLabel}>{label}</span>
            <div className={styles.scopeActions}>
                {actions.map(action => (
                    <Checkbox
                        key={action}
                        label={API_SCOPE_ACTION_LABELS[action]}
                        checked={hasScope(buildScope(resource, action))}
                        onChange={checked => handleScopeChange(resource, action, checked)}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <CenteredModal
            isOpen={isOpen}
            onClose={onClose}
            maxWidth="medium"
            className={styles.addApiKeyModal}
        >
            <CenteredModal.Header
                title={isEditing ? 'Edit API Key' : 'Create API Key'}
                showCloseButton={true}
            />

            <CenteredModal.Content className={styles.modalContent}>
                <form
                    ref={formRef}
                    className={styles.form}
                    onSubmit={event => event.preventDefault()}
                >
                    <div className={styles.formField}>
                        <Input
                            id="apiKeyName"
                            label="Name"
                            value={name}
                            onChange={event => {
                                setName(event.target.value);
                                setErrors(previous => ({ ...previous, name: undefined }));
                            }}
                            placeholder="What will use this key, for example Snowflake loader"
                            error={errors.name}
                            maxLength={100}
                        />
                    </div>

                    <div className={styles.formField}>
                        <Input
                            id="apiKeyExpiresAt"
                            type="date"
                            label="Expiration date"
                            value={expiresAt}
                            min={earliestExpiry()}
                            onChange={event => {
                                setExpiresAt(event.target.value);
                                setErrors(previous => ({ ...previous, expiresAt: undefined }));
                            }}
                            error={errors.expiresAt}
                        />
                        <p className={styles.fieldHint}>Leave empty for a key that never expires.</p>
                    </div>

                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Permissions</h3>
                        <p className={styles.sectionHint}>
                            Grant only what the integration needs. A request made with a key that lacks a permission is
                            rejected.
                        </p>
                        {errors.scopes && <p className={styles.fieldError}>{errors.scopes}</p>}

                        <div className={styles.scopeList}>{availableResources.map(renderScopeRow)}</div>
                    </section>

                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>Location access</h3>
                        {errors.locations && <p className={styles.fieldError}>{errors.locations}</p>}

                        <Checkbox
                            label="All locations, including any added later"
                            checked={allLocations}
                            onChange={checked => {
                                setAllLocations(checked);
                                setErrors(previous => ({ ...previous, locations: undefined }));
                            }}
                        />

                        {!allLocations && (
                            <div className={styles.locationList}>
                                {locations.map(location => (
                                    <Checkbox
                                        key={location.id}
                                        label={location.businessName}
                                        checked={locationIds.includes(location.id)}
                                        onChange={checked => toggleLocation(location.id, checked)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Form Actions */}
                    <div className={styles.formActions}>
                        <Button
                            label="Cancel"
                            variant="outlined"
                            size="large"
                            onClick={onClose}
                            disabled={isSaving}
                        />
                        <Button
                            label={isEditing ? 'Save changes' : 'Create API Key'}
                            variant="black"
                            size="large"
                            onClick={handleSave}
                            disabled={isSaving}
                        />
                    </div>
                </form>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default AddApiKeyModal;
